import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Table, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import SoftBadge from 'components/common/SoftBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import InfiniteScroll from 'react-infinite-scroll-component';

const SeccionOrdenesPagadasLicencia = ({ idLicencia }) => {
    const { token, setToken } = useToken();
    const { showErrorToast } = useToast();
    const [cargando, setCargando] = useState(true);
    const [ordenes, setOrdenes] = useState([]);
    const [pagina, setPagina] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [totalOrdenes, setTotalOrdenes] = useState(0);

    const fetchOrdenesPagadas = async (paginaAObtener = 1) => {
        if (!idLicencia) return;
        try {
            const response = await fetch(`/api/licencia/ObtenerOrdenesPagadasVinculadas?idLicencia=${idLicencia}&pagina=${paginaAObtener}&tamanioPagina=5`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1 && data.detalles) {
                    const parsed = JSON.parse(data.detalles);
                    const nuevasOrdenes = parsed.table2 || parsed.Table2 || [];
                    const total = parsed.table3?.[0]?.totalOrdenes || parsed.Table3?.[0]?.totalOrdenes || 0;

                    setTotalOrdenes(total);

                    if (paginaAObtener === 1) {
                        setOrdenes(nuevasOrdenes);
                    } else {
                        setOrdenes(prev => [...prev, ...nuevasOrdenes]);
                    }

                    if (nuevasOrdenes.length < 5 || (paginaAObtener === 1 && nuevasOrdenes.length >= total)) {
                        setHasMore(false);
                    } else {
                        setPagina(paginaAObtener + 1);
                    }
                } else {
                    setHasMore(false);
                }
            } else {
                if (response.status === 401) {
                    setToken(null);
                    window.location.href = '';
                }
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error al obtener órdenes vinculadas:', error);
            showErrorToast('Error al cargar las órdenes vinculadas');
            setHasMore(false);
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        setOrdenes([]);
        setPagina(1);
        setHasMore(true);
        setCargando(true);
        fetchOrdenesPagadas(1);
    }, [idLicencia]);

    return (
        <Card className="mb-3">
            <Card.Header className="bg-light d-flex align-items-center justify-content-between py-2">
                <h5 className="mb-0 text-primary">
                    <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2" />
                    &Oacute;rdenes Vinculadas
                </h5>
                <SoftBadge pill bg="success">
                    {totalOrdenes} {totalOrdenes === 1 ? 'Orden' : '\u00d3rdenes'}
                </SoftBadge>
            </Card.Header>
            <Card.Body className="p-0">
                {cargando ? (
                    <div className="p-3 text-center">
                        <Spinner animation="border" variant="primary" size="sm" /> Cargando &oacute;rdenes...
                    </div>
                ) : ordenes.length === 0 ? (
                    <div className="p-3 text-muted text-center fs--1">
                        No hay &oacute;rdenes vinculadas a esta licencia.
                    </div>
                ) : (
                    <div
                        className="border-top"
                        style={{ maxHeight: '280px', overflowY: 'auto' }}
                        id={`scrollableTargetOrdenesLicencia-${idLicencia}`}
                    >
                        <InfiniteScroll
                            dataLength={ordenes.length}
                            next={() => fetchOrdenesPagadas(pagina)}
                            hasMore={hasMore}
                            loader={
                                <div className="d-flex justify-content-center align-items-center py-2">
                                    <Spinner animation="border" size="sm" variant="primary" />
                                </div>
                            }
                            scrollableTarget={`scrollableTargetOrdenesLicencia-${idLicencia}`}
                            scrollThreshold={0.75}
                            endMessage={
                                ordenes.length > 5 && (
                                    <div className="text-center py-2">
                                        <small className="text-muted">Sin m&aacute;s &oacute;rdenes</small>
                                    </div>
                                )
                            }
                        >
                            <Table responsive hover striped className="fs--1 mb-0 align-middle">
                                <thead
                                    className="bg-200 text-900"
                                    style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f9fbfd' }}
                                >
                                    <tr>
                                        <th scope="col"># Orden</th>
                                        <th scope="col">Fecha Venta</th>
                                        <th scope="col">Vendedor / Distribuidor</th>
                                        <th scope="col">Tipo Compra</th>
                                        <th scope="col">Monto</th>
                                        <th scope="col" className="text-center">Estatus</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ordenes.map(orden => (
                                        <tr key={orden.idOrden}>
                                            <td className="fw-bold">
                                                <Link to={`/ordenes/detalle/${orden.idOrden}`} target="_blank" rel="noopener noreferrer">
                                                    #{orden.idOrden}
                                                </Link>
                                            </td>
                                            <td>
                                                {orden.dFechaVenta ? new Date(orden.dFechaVenta).toLocaleDateString('es-MX', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                }) : '-'}
                                            </td>
                                            <td>
                                                <span>{orden.vNombreVendedor || '-'}</span>
                                            </td>
                                            <td>
                                                <SoftBadge pill bg={orden.bIsRecompra ? 'primary' : 'success'}>
                                                    {orden.bIsRecompra ? 'Re-compra' : 'Primer Compra'}
                                                </SoftBadge>
                                            </td>
                                            <td className="fw-semi-bold">
                                                ${parseFloat(orden.dTotal || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {orden.vMoneda || 'MXN'}
                                            </td>
                                            <td className="text-center">
                                                {(() => {
                                                    const isActive = orden.bIsActive ?? orden.bEstado;
                                                    if (isActive === 0 || isActive === false || isActive === "0") {
                                                        return <SoftBadge pill bg="danger">Desactivada</SoftBadge>;
                                                    }
                                                    if (orden.bIsPagada === 1 || orden.bIsPagada === true || orden.bIsPagada === "1") {
                                                        return <SoftBadge pill bg="success">Pagada</SoftBadge>;
                                                    }
                                                    return <SoftBadge pill bg="warning">Por pagar</SoftBadge>;
                                                })()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </InfiniteScroll>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

SeccionOrdenesPagadasLicencia.propTypes = {
    idLicencia: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default SeccionOrdenesPagadasLicencia;
